<template lang="pug">
b-modal#unbindOTPTelegramModal(
  ref="unbindOTPTelegramModal"
  :title="$t('title.unbind_otp_telegram')"
  centered
  no-fade
  no-close-on-backdrop
  :busy="loading"
  size="md"
  @ok="done"
  @show="reset"
  @shown="shown"
  @hide="reset"
)
  b-form(novalidate :disabled="loading" @submit.stop.prevent="submit")
    .form-group
      label {{ $t("ui.otp") }}
      b-input-group
        DebounceInput(
          type="text"
          inputmode="numeric"
          pattern="[0-9]*"  
          ref="unbind_telegram_code"
          :dirty="$v.unbind_telegram_code.$dirty"
          :error="$v.unbind_telegram_code.$error"
          :placeholder='$t("ui.enter_6_digit_code")'
          v-model.trim.lazy="$v.unbind_telegram_code.$model"
          :maxLength="6"
          :disabled="loading"
          class="otp-input-field"
        )
        .input-group-append
          b-button(
            variant="info"
            size="sm"
            @click="getTelegramOTP"
            :disabled="loading || countdownTimer > 0"
            class="get-otp-btn"
          )
            span(v-if="countdownTimer > 0") {{ countdownTimer }}s
            span(v-else) {{ $t("ui.get_otp") || "Get OTP" }}
      ValidateResult(:value="$v.unbind_telegram_code" :name="$t('ui.otp')")
    .form-group
      b-alert.alert-sm.animated.flash(:show="!submission.success" variant="danger") {{ $t(submission.status) }}
  div(slot="modal-cancel")
    SubmitButton(:loading="loading" icon="fa fa-times" :text="$t('ui.cancel')" isModal=true)
  div(slot="modal-ok")
    SubmitButton(:loading="loading" icon="fa fa-check" :text="$t('ui.submit')" isModal=true)
</template>

<script>
import { required, numeric, minLength, maxLength } from "vuelidate/lib/validators";
import SubmitButton from "@/library/SubmitButton";
import ValidateResult from "@/library/ValidateResult";
import DebounceInput from "@/library/DebounceInput";

export default {
  components: {
    SubmitButton,
    ValidateResult,
    DebounceInput
  },
  data() {
    return {
      unbind_telegram_code: "",
      loading: false,
      submission: {
        success: true,
        status: ""
      },
      countdownTimer: 0,
      countdownInterval: null
    };
  },
  validations: {
    unbind_telegram_code: {
      required,
      numeric,
      minLength: minLength(6),
      maxLength: maxLength(6)
    }
  },
  methods: {
    done(event) {
      event.preventDefault();
      this.submit();
    },
    reset() {
      this.unbind_telegram_code = "";
      this.submission.success = true;
      this.submission.status = "";
      this.$v.unbind_telegram_code.$reset();
      this.stopCountdown();
    },
    stopCountdown() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
      this.countdownTimer = 0;
    },
    startCountdown() {
      this.stopCountdown();
      this.countdownTimer = 30;
      this.countdownInterval = setInterval(() => {
        if (this.countdownTimer > 0) {
          this.countdownTimer -= 1;
        } else {
          this.stopCountdown();
        }
      }, 1000);
    },
    async getTelegramOTP() {
      if (this.countdownTimer > 0 || this.loading) {
        return;
      }
      
      this.loading = true;
      this.submission.success = true;
      this.submission.status = "";
      
      try {
        const response = await this.$store.dispatch("user/requestOTPTelegramAfter");
        if (response.success) {
          this.startCountdown();
          this.$swal({
            title: this.$t("ui.succeed"),
            text: this.$t("message.telegram_otp_sent") || "OTP code has been sent to your Telegram",
            type: "success",
            timer: 2000,
            showConfirmButton: false
          });
        } else {
          this.submission.success = false;
          this.submission.status = this.$t("message." + (response.errorTag || "telegram_otp_request_failed"));
        }
      } catch (error) {
        this.submission.success = false;
        this.submission.status = this.$t("message.telegram_otp_request_failed") || "Failed to request OTP";
      } finally {
        this.loading = false;
      }
    },
    shown() {
      this.$nextTick(() => {
        if (this.$refs.unbind_telegram_code) {
          this.$refs.unbind_telegram_code.$el.focus();
        }
      });
    },
    async submit() {
      this.$v.unbind_telegram_code.$touch();
      
      if (this.$v.unbind_telegram_code.$invalid) {
        return;
      }
      
      this.loading = true;
      this.submission.success = true;
      this.submission.status = "";
      
      try {
        const response = await this.$store.dispatch("user/unbindOTPTelegram", {
          code: this.unbind_telegram_code
        });
        
        if (response.success) {
          this.$refs.unbindOTPTelegramModal.hide();
          this.$swal({
            title: this.$t("ui.succeed"),
            text: this.$t("message.otp_unbind_success"),
            type: "success",
            allowOutsideClick: false
          }).then(() => {
            this.$emit("unbind-success");
          });
        } else {
          this.submission.success = false;
          this.submission.status = this.$t("message." + (response.errorTag || "otp_unbind_failed"));
        }
      } catch (error) {
        this.submission.success = false;
        this.submission.status = this.$t("message.otp_unbind_failed");
      } finally {
        this.loading = false;
      }
    }
  },
  beforeDestroy() {
    this.stopCountdown();
  }
};
</script>

<style scoped>
.otp-input-field {
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 0.5rem;
  font-weight: bold;
  padding: 12px;
  min-width: 200px;
  flex: 1;
}

.otp-input-field::placeholder {
  font-size: 1.5rem;
  letter-spacing: 0.1rem;
  opacity: 0.6;
}

.get-otp-btn {
  white-space: nowrap;
  min-width: 100px;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>

